import CscaExplanationLanguageSwitch, { useCscaExplanationLanguage } from '../components/CscaExplanationLanguage';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckSquare, FileDown, Printer, Settings, Sparkles, Square } from 'lucide-react';
import { api, W1GrammarAnalysisResponse } from '../utils/api';
import { printHtml } from '../utils/print';
import { useAuthStore } from '../store';
import { isAdminRole } from '../utils/permissions';
import MathText from '../components/MathText';
import { getCscaModeDetail, getCscaTestTitle } from '../utils/cscaTestPresentation';
import { PrintablePart, PartFilter, getPrintablePart, getPrintableParts, getSectionTitle, getSectionHelp, formatWrongPrintText, resolveWrongImage } from '../utils/adminWrongQuestions';
import AdminWrongText from '../components/AdminWrongText';
import { buildW1GrammarPrintSections, W1_GRAMMAR_PRINT_CSS, GrammarPrintVariant } from '../utils/w1GrammarPrint';
import { downloadTemplateExport } from '../utils/templatePdf';
import { useW2SentenceAnalysis } from '../hooks/useW2SentenceAnalysis';
import W2SentenceGuide from '../components/W2SentenceGuide';


interface AdminUserSummary {
  id?: string;
  username?: string;
}

interface WrongHistoryGroup {
  id: string;
  date: string;
  level: number;
  part: string;
  bank_version: string;
  system_type?: string;
  paper_id?: string | null;
  paper_year?: number | null;
  paper_month?: number | null;
  topic_coarse?: string | null;
  topic_fine?: string | null;
  items: Array<{
    id: string;
    question_id: string;
    question: string;
    options: string[];
    correctAnswer: number | null;
    answer: number | null;
    answerContent: string[] | string | { sequence?: string[]; text?: string } | null;
    chose_buzhidao: boolean;
    part: string;
    explanation?: string;
  explanation_id?: string;
  correctAnswers?: number[];
    media?: string | null;
  }>;
}

interface AggregatedWrongQuestion {
  id: string;
  source_test_id: string;
  source_test_date: string;
  source_test_part: string;
  source_test_label: string;
  question_id: string;
  question_text: string;
  options: string[];
  part: string;
  printablePart: PrintablePart;
  correct_option_index: number | null;
  chosen_option_index: number | null;
  answer_content: string[] | string | null;
  explanation?: string;
  explanation_id?: string;
  correctAnswers?: number[];
  media?: string | null;
}


const getAnswerLines = (count: number) => Array.from({ length: count }, (_, index) => `line-${index}`);

const escapeHtml = (value: string) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatPrintText = formatWrongPrintText;

const resolveMediaUrl = (media?: string | null) => {
  if (!media) return '';
  if (media.startsWith('http://') || media.startsWith('https://')) return media;
  const base = BACKEND_URL.replace(/\/$/, '');
  return `${base}/media${media.startsWith('/') ? '' : '/'}${media}`;
};

const buildPrintableHtml = ({
  userName,
  bankVersion,
  level,
  items,
  grammarSections = ''
}: {
  userName: string;
  bankVersion: string;
  level: number;
  items: AggregatedWrongQuestion[];
  grammarSections?: string;
}) => {
  const plainItems = grammarSections ? items.filter(item => item.printablePart !== 'W1') : items;
  const printedCount = grammarSections ? plainItems.length + new Set(items.filter(item => item.printablePart === 'W1').map(item => item.question_id)).size : items.length;
  const grouped = getPrintableParts(plainItems)
    .map((part) => ({
      part,
      items: plainItems.filter((item) => item.printablePart === part)
    }))
    .filter((group) => group.items.length > 0);

  const sections = grouped.map((group, sectionIndex) => `
    <section class="print-section ${sectionIndex > 0 ? 'page-break' : ''}">
      <header class="section-header">
        <div class="section-title">${escapeHtml(getSectionTitle(group.part))}</div>
        <div class="section-subtitle">${escapeHtml(getSectionHelp(group.part))}</div>
      </header>
      ${group.items.map((item, index) => {
        const mediaUrl = resolveMediaUrl(item.media);
        const optionsHtml = item.printablePart !== 'W1' && item.options.length > 0 ? `
          <div class="options">
            ${item.options.map((option, optionIndex) => `
              <div class="option-row">
                <span class="option-label">${String.fromCharCode(65 + optionIndex)}.</span>
                <span class="option-text">${formatPrintText(option)}</span>
              </div>
            `).join('')}
          </div>
        ` : '';

        const answerHtml = group.part.startsWith('R') || group.part === 'MATH'
          ? `
            <div class="answer-box">
              <span class="answer-label">Answer:</span>
              <span class="choice-circle"></span>
              <span class="choice-circle"></span>
              <span class="choice-circle"></span>
              <span class="choice-circle"></span>
            </div>
          `
          : `
            <div class="answer-area">
              <div class="answer-label">Answer:</div>
              ${getAnswerLines(group.part === 'W1' ? 5 : 3).map(() => '<div class="answer-line"></div>').join('')}
            </div>
          `;

        return `
          <article class="question-card">
            <div class="question-number">${index + 1}.</div>
            <div class="question-body">
              <div class="question-text">${formatPrintText(item.question_text)}</div>
              ${resolveWrongImage(item.media) ? formatPrintText(item.media || '') : mediaUrl ? `<div class="media-note">Media: <a href="${escapeHtml(mediaUrl)}">${escapeHtml(mediaUrl)}</a></div>` : ''}
              ${optionsHtml}
              ${answerHtml}
            </div>
          </article>
        `;
      }).join('')}
    </section>
  `).join('');

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <title>All Wrong Questions</title>
      <style>
        ${W1_GRAMMAR_PRINT_CSS}
        * { box-sizing: border-box; }
        body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 0; padding: 28px; line-height: 1.5; }
        .sheet-header { border-bottom: 2px solid #d1d5db; padding-bottom: 16px; margin-bottom: 24px; }
        .sheet-title { font-size: 24px; font-weight: 700; margin-bottom: 10px; }
        .meta-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 20px; font-size: 14px; }
        .meta-label { color: #6b7280; display: inline-block; min-width: 72px; }
        .page-break { page-break-before: always; break-before: page; }
        .section-header { margin-bottom: 18px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; }
        .section-title { font-size: 20px; font-weight: 700; }
        .section-subtitle { font-size: 13px; color: #6b7280; margin-top: 4px; }
        .question-card { display: flex; gap: 12px; margin-bottom: 20px; page-break-inside: avoid; break-inside: avoid; }
        .question-number { font-weight: 700; font-size: 18px; min-width: 28px; }
        .question-body { flex: 1; }
        .question-text { font-size: 16px; font-weight: 600; margin-bottom: 10px; }
        .media-note { font-size: 12px; color: #4b5563; margin-bottom: 8px; word-break: break-all; }
        .options { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #f9fafb; margin-bottom: 12px; }
        .option-row { display: flex; gap: 10px; margin-bottom: 8px; }
        .option-row:last-child { margin-bottom: 0; }
        .option-label { width: 20px; font-weight: 700; }
        .option-text { flex: 1; }
        .answer-box { display: flex; align-items: center; gap: 10px; margin-top: 10px; min-height: 24px; }
        .answer-area { margin-top: 12px; }
        .answer-label { font-size: 13px; color: #374151; margin-bottom: 6px; font-weight: 600; }
        .choice-circle { width: 18px; height: 18px; border: 1.5px solid #6b7280; border-radius: 9999px; display: inline-block; }
        .answer-line { border-bottom: 1px solid #9ca3af; height: 28px; margin-bottom: 10px; }
        @page { size: A4; margin: 14mm; }
      </style>
    </head>
    <body>
      <header class="sheet-header">
        <div class="sheet-title">Wrong Questions</div>
        <div class="meta-grid">
          <div><span class="meta-label">Name</span>${escapeHtml(userName)}</div>
          <div><span class="meta-label">Exam Type</span>${bankVersion === 'CSCA' ? 'CSCA' : `HSK ${escapeHtml(bankVersion)}`}</div>
          <div><span class="meta-label">Level</span>${bankVersion === 'CSCA' ? `CSCA ${escapeHtml(String(level))}` : `HSK ${escapeHtml(String(level))}`}</div>
          <div><span class="meta-label">Count</span>${escapeHtml(String(printedCount))}</div>
        </div>
      </header>
      ${grammarSections}
      ${sections}
    </body>
    </html>
  `;
};

const AdminStudentWrongHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user: currentUser } = useAuthStore();
  const [searchParams] = useSearchParams();
  const user = (location.state?.user || {}) as AdminUserSummary;

  const level = Number(searchParams.get('level') || 0);
  const bankVersion = searchParams.get('bankVersion') || '';
  const category = searchParams.get('category') || 'simulation';

  const [loading, setLoading] = useState(true);
  const { language: explanationLanguage, changeLanguage: changeExplanationLanguage } = useCscaExplanationLanguage();
  const [error, setError] = useState('');
  const [partFilter, setPartFilter] = useState<PartFilter>('ALL');
  const [questions, setQuestions] = useState<AggregatedWrongQuestion[]>([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [grammarAnalysis, setGrammarAnalysis] = useState<W1GrammarAnalysisResponse | null>(null);
  const [grammarAnalysisLoading, setGrammarAnalysisLoading] = useState(false);
  const [grammarAnalysisError, setGrammarAnalysisError] = useState('');
  const [generatingWorkbook, setGeneratingWorkbook] = useState<'student' | 'teacher' | null>(null);
  const [printing, setPrinting] = useState(false);
  const [sentenceExporting, setSentenceExporting] = useState<string | null>(null);
  const w2 = useW2SentenceAnalysis(id, token, category, bankVersion, level);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token || !id || !level || !bankVersion) {
        setError('Please choose one HSK type and one level first.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setQuestions([]);
      setSelectedQuestionIds([]);
      setPartFilter('ALL');
      setGrammarAnalysis(null);
      setGrammarAnalysisError('');
      try {
        const res = await api.getAdminUserWrongHistory(id, token, { level, bankVersion, category });
        if (!active) return;
        const flattened = ((res.history || []) as WrongHistoryGroup[])
          .filter(group => Number(group.level) === level && group.bank_version === bankVersion)
          .flatMap((group) =>
            (group.items || []).map((item, index) => {
              const printablePart = getPrintablePart(item.part, group.bank_version);
              if (!printablePart) return null;
              return {
                id: `${group.id}-${item.id || item.question_id}-${index}`,
                source_test_id: group.id,
                source_test_date: group.date,
                source_test_part: group.part,
                source_test_label: group.bank_version === 'CSCA'
                  ? `${getCscaTestTitle(group)}${getCscaModeDetail(group) ? ` · ${getCscaModeDetail(group)}` : ''}`
                  : `HSK ${group.level} · ${group.part}`,
                question_id: item.question_id,
                question_text: item.question,
                options: Array.isArray(item.options) ? item.options.map((option) => String(option ?? '')) : [],
                part: item.part,
                printablePart,
                correct_option_index: Number.isInteger(item.correctAnswer) ? item.correctAnswer : null,
                chosen_option_index: Number.isInteger(item.answer) ? item.answer : null,
                answer_content: Array.isArray(item.answerContent)
                  ? item.answerContent.map((entry) => String(entry ?? ''))
                  : typeof item.answerContent === 'string'
                    ? item.answerContent
                    : item.answerContent && typeof item.answerContent === 'object' && Array.isArray(item.answerContent.sequence)
                      ? item.answerContent.sequence.map((entry) => String(entry ?? ''))
                      : item.answerContent && typeof item.answerContent === 'object' && typeof item.answerContent.text === 'string'
                        ? item.answerContent.text
                        : null,
                explanation: item.explanation || '',
                explanation_id: item.explanation_id,
                correctAnswers: item.correctAnswers,
                media: item.media ? String(item.media) : null
              } as AggregatedWrongQuestion;
            })
          )
          .filter(Boolean) as AggregatedWrongQuestion[];

        setQuestions(flattened);
        setSelectedQuestionIds(flattened.map((item) => item.id));
      } catch (err: any) {
        if (!active) return;
        console.error(err);
        setError(err?.message || 'Failed to load wrong questions');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [bankVersion, category, id, level, token]);

  const visibleQuestions = useMemo(() => {
    if (partFilter === 'ALL') return questions;
    return questions.filter((item) => item.printablePart === partFilter);
  }, [partFilter, questions]);
  const partFilters: PartFilter[] = ['ALL', ...getPrintableParts(questions)];

  const selectedQuestions = useMemo(
    () => questions.filter((item) => selectedQuestionIds.includes(item.id)),
    [questions, selectedQuestionIds]
  );

  const uniqueW1Count = useMemo(
    () => new Set(questions.filter((item) => item.printablePart === 'W1').map((item) => item.question_id)).size,
    [questions]
  );
  const aiAnalysisAvailable = bankVersion === '2.0' && (level === 3 || level === 4) && uniqueW1Count > 0;
  const w2Available = bankVersion === '2.0' && level === 4;
  const selectedW2 = useMemo(() => selectedQuestions.filter(item => item.printablePart === 'W2'), [selectedQuestions]);
  const w2Guides = useMemo(() => new Map(w2.result?.guides.map(guide => [guide.question_id, guide]) || []), [w2.result]);

  const visibleSelectedCount = visibleQuestions.filter((item) => selectedQuestionIds.includes(item.id)).length;

  const toggleQuestion = (questionId: string) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(questionId) ? prev.filter((idValue) => idValue !== questionId) : [...prev, questionId]
    );
  };

  const selectVisible = () => {
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      visibleQuestions.forEach((item) => next.add(item.id));
      return Array.from(next);
    });
  };

  const clearVisible = () => {
    const visibleIds = new Set(visibleQuestions.map((item) => item.id));
    setSelectedQuestionIds((prev) => prev.filter((itemId) => !visibleIds.has(itemId)));
  };

  const exportJson = () => {
    if (selectedQuestions.length === 0) return;

    const payload = {
      version: '1.0',
      exported_at: new Date().toISOString(),
      source: 'admin-all-wrong-questions',
      user: {
        id,
        username: user?.username || `User ${id}`
      },
      filter: {
        bankVersion,
        level
      },
      items: selectedQuestions.map((item) => ({
        source_test_id: item.source_test_id,
        source_test_date: item.source_test_date,
        source_test_part: item.source_test_part,
        question_id: item.question_id,
        part: item.part,
        printable_part: item.printablePart,
        question_text: item.question_text,
        options: item.options,
        media: item.media,
        student_answer: {
          chosen_option_index: item.chosen_option_index,
          answer_content: item.answer_content
        },
        correct_answer: {
          correct_option_index: item.correct_option_index
        },
        explanation: item.explanation || ''
      }))
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const safeName = (user?.username || `user-${id}`).replace(/[^\w-]+/g, '_');
    anchor.href = url;
    anchor.download = `${safeName}-${bankVersion === 'CSCA' ? 'csca' : `hsk-${bankVersion}`}-level-${level}-all-wrong.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const printSelected = async (variant: GrammarPrintVariant = 'student', grammarOnly = false) => {
    const items = grammarOnly ? selectedQuestions.filter(item => item.printablePart === 'W1') : selectedQuestions;
    if (items.length === 0 || printing) return;
    setPrinting(true);
    setGrammarAnalysisError('');
    try {
      const w1 = items.filter(item => item.printablePart === 'W1');
      let grammarSections = '';
      if (aiAnalysisAvailable && w1.length) {
        const result = grammarAnalysis || await analyzeW1Grammar();
        if (!result) return;
        grammarSections = buildW1GrammarPrintSections(result, w1, variant);
      }
      const html = buildPrintableHtml({ userName: user?.username || `User ${id}`, bankVersion, level, items, grammarSections });
      printHtml(html, 'Unable to open print preview.');
    } catch (printError: any) {
      setGrammarAnalysisError(printError?.message || 'Failed to prepare the grammar PDF.');
    } finally {
      setPrinting(false);
    }
  };

  const analyzeW1Grammar = async () => {
    if (!token || !id || !aiAnalysisAvailable) return null;
    setGrammarAnalysisLoading(true);
    setGrammarAnalysisError('');
    setGrammarAnalysis(null);
    try {
      const result = await api.analyzeAdminUserW1Grammar(id, token, bankVersion, level);
      setGrammarAnalysis(result);
      return result;
    } catch (analysisError: any) {
      setGrammarAnalysisError(analysisError?.message || 'Failed to analyze W1 grammar.');
      return null;
    } finally {
      setGrammarAnalysisLoading(false);
    }
  };

  const downloadGrammarWorkbook = async (variant: 'student' | 'teacher') => {
    if (!grammarAnalysis) return;
    setGeneratingWorkbook(variant);
    setGrammarAnalysisError('');
    try {
      if (!token || !id) return;
      const selected = selectedQuestions.filter(item => item.printablePart === 'W1');
      const items = selected.length ? selected : questions.filter(item => item.printablePart === 'W1');
      await downloadTemplateExport(token, { studentId: id, userName: user?.username || `User ${id}`,
        bankVersion, level, category, items, grammar: grammarAnalysis, variant, format: 'docx' });
    } catch (downloadError: any) {
      setGrammarAnalysisError(downloadError?.message || 'Failed to generate the Word document.');
    } finally {
      setGeneratingWorkbook(null);
    }
  };

  const exportW2 = async (variant: GrammarPrintVariant) => {
    if (!token || !id || !selectedW2.length || sentenceExporting) return;
    setSentenceExporting(`docx-${variant}`);
    setGrammarAnalysisError('');
    try {
      const result = await w2.analyze(selectedW2);
      if (!result) return;
      await downloadTemplateExport(token, { studentId: id, userName: user?.username || `User ${id}`,
        bankVersion, level, category, items: selectedW2, sentenceAnalysis: result, variant, format: 'docx' });
    } catch (error: any) {
      setGrammarAnalysisError(error?.message || '造句练习文档生成失败，请重试。');
    } finally { setSentenceExporting(null); }
  };

  const downloadSelectedWord = async () => {
    if (!token || !id || !selectedQuestions.length || generatingWorkbook) return;
    setGeneratingWorkbook('student');
    setGrammarAnalysisError('');
    try {
      const hasW1 = aiAnalysisAvailable && selectedQuestions.some(item => item.printablePart === 'W1');
      const analysis = hasW1 ? grammarAnalysis || await analyzeW1Grammar() : null;
      if (hasW1 && !analysis) return;
      const sentenceAnalysis = w2Available && selectedW2.length ? await w2.analyze(selectedW2) : null;
      if (w2Available && selectedW2.length && !sentenceAnalysis) return;
      await downloadTemplateExport(token, {studentId:id,userName:user?.username || `User ${id}`,
        bankVersion,level,category,items:selectedQuestions,grammar:analysis,sentenceAnalysis,variant:'student',format:'docx'});
    } catch (error: any) { setGrammarAnalysisError(error?.message || 'Word 文档生成失败，请重试。'); }
    finally { setGeneratingWorkbook(null); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow">
        <button
          onClick={() => navigate(location.state?.returnTo || `/admin/users/${id}/history?bankVersion=${bankVersion}&category=${category}&level=${level}`, { state: { user } })}
          className="mb-3 inline-flex items-center text-sm font-medium text-slate-500 transition hover:text-indigo-600"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back To Test History
        </button>
        <h1 className="text-2xl font-bold text-slate-900">View All Wrong</h1>
        <p className="mt-2 text-sm text-slate-500">
          {user?.username ? `${user.username} · ` : ''}{bankVersion === 'CSCA' ? 'CSCA 数学' : `HSK ${bankVersion || '-'}`} · Level {level || '-'}
        </p>
      </div>

      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-slate-200 px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {partFilters.map((option) => {
                const active = partFilter === option;
                return (
                  <button
                    key={option}
                    onClick={() => setPartFilter(option)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                      active ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {option === 'ALL' ? 'All' : option}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {bankVersion === '2.0' && (level === 3 || level === 4) && isAdminRole(currentUser?.role) && (
                <button
                  onClick={() => navigate(`/admin/w1-grammar-categories?bankVersion=2.0&level=${level}`)}
                  className="inline-flex items-center rounded-lg border border-indigo-200 px-3 py-2 text-sm font-medium text-indigo-700 transition hover:bg-indigo-50"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Grammar Settings
                </button>
              )}
              {bankVersion === '2.0' && (level === 3 || level === 4) && (
                <button
                  onClick={() => void analyzeW1Grammar()}
                  disabled={!aiAnalysisAvailable || grammarAnalysisLoading || printing || w2.loading || sentenceExporting !== null}
                  className="inline-flex items-center rounded-lg bg-violet-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                  title={uniqueW1Count === 0 ? 'No wrong W1 questions found' : undefined}
                >
                  <Sparkles className={`mr-2 h-4 w-4 ${grammarAnalysisLoading ? 'animate-pulse' : ''}`} />
                  {grammarAnalysisLoading ? 'AI Analyzing...' : 'AI分析'}
                </button>
              )}
              {w2Available && (
                <button
                  onClick={() => void w2.analyze(selectedW2, true)}
                  disabled={!selectedW2.length || w2.loading || grammarAnalysisLoading || printing || sentenceExporting !== null}
                  className="inline-flex items-center rounded-lg bg-emerald-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
                  title="分析已勾选的 W2 造句题：生词词性、简单句型和参考例句"
                >
                  <Sparkles className={`mr-2 h-4 w-4 ${w2.loading ? 'animate-pulse' : ''}`} />
                  {w2.loading ? '正在分析造句…' : 'W2 造句分析'}
                </button>
              )}
              {bankVersion === 'CSCA' && <CscaExplanationLanguageSwitch language={explanationLanguage} onChange={changeExplanationLanguage} />}
              <button
                onClick={selectVisible}
                disabled={visibleQuestions.length === 0}
                className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CheckSquare className="mr-2 h-4 w-4" />
                Select Visible
              </button>
              <button
                onClick={clearVisible}
                disabled={visibleSelectedCount === 0}
                className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Square className="mr-2 h-4 w-4" />
                Clear Visible
              </button>
              <button
                onClick={exportJson}
                disabled={selectedQuestions.length === 0}
                className="inline-flex items-center rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileDown className="mr-2 h-4 w-4" />
                Export JSON
              </button>
              <button
                onClick={() => void printSelected()}
                disabled={selectedQuestions.length === 0 || printing || grammarAnalysisLoading || w2.loading || sentenceExporting !== null}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Printer className="mr-2 h-4 w-4" />
                {printing ? 'Preparing PDF...' : 'Print PDF'}
              </button>
              {bankVersion !== 'CSCA' && (
                <button onClick={() => void downloadSelectedWord()}
                  disabled={!selectedQuestions.length || generatingWorkbook !== null || printing || w2.loading || grammarAnalysisLoading || sentenceExporting !== null}
                  className="inline-flex items-center rounded-lg bg-indigo-700 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-800 disabled:opacity-50">
                  <FileDown className="mr-2 h-4 w-4" />{generatingWorkbook ? '正在生成 Word…' : '下载 Word'}
                </button>
              )}
            </div>
          </div>

          {aiAnalysisAvailable && <p className="mt-3 text-sm text-slate-500">PDF 中的 W1 错题按语法分类，包含语法结构、用法说明、例句和练习题；默认打印学生练习版。</p>}
          {w2Available && <p className="mt-2 text-sm text-slate-500">W2 造句分析会为已勾选的造句题补充生词词性、词义、简单句型和例句拆解；下载 Word 时也会包含这些学习提示。</p>}

          <div className="mt-4 text-sm text-slate-600">
            <span>{visibleQuestions.length} visible</span>
            <span className="mx-2 text-slate-300">|</span>
            <span>{selectedQuestions.length} selected</span>
            <span className="mx-2 text-slate-300">|</span>
            <span>{visibleSelectedCount} selected in current filter</span>
            {bankVersion === '2.0' && (level === 3 || level === 4) && (
              <>
                <span className="mx-2 text-slate-300">|</span>
                <span>{uniqueW1Count} unique W1 for AI analysis</span>
              </>
            )}
          </div>

          {(grammarAnalysisError || w2.error) && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {grammarAnalysisError || w2.error}
            </div>
          )}

          {w2.result && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="font-semibold text-emerald-900">W2 造句分析完成 · {w2.result.question_count} 道题</p>
              <p className="mt-1 text-sm text-emerald-800">每道题下方已显示词性、简单句型和参考例句。勾选要导出的 W2 题目：</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(['student', 'teacher'] as const).map(variant => (
                  <button key={variant} onClick={() => void exportW2(variant)}
                    disabled={!selectedW2.length || w2.loading || printing || sentenceExporting !== null || generatingWorkbook !== null}
                    className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
                    {sentenceExporting === `docx-${variant}` ? '正在生成…' : `W2 ${variant === 'student' ? '学生练习版' : '教师参考版'} Word`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {grammarAnalysis && (
            <div className="mt-4 rounded-2xl border border-violet-200 bg-violet-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 font-semibold text-violet-900">
                    <Sparkles className="h-4 w-4" /> AI analysis complete
                  </div>
                  <p className="mt-1 text-sm text-violet-700">
                    {grammarAnalysis.question_count} unique W1 questions · {grammarAnalysis.categories.length} grammar categories
                    {grammarAnalysis.fallback_count > 0 ? ` · ${grammarAnalysis.fallback_count} in Other` : ''}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {grammarAnalysis.categories.map((category) => (
                      <span key={category.id} className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-violet-700 shadow-sm">
                        {category.name} {category.questions.length}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => void printSelected('student', true)} disabled={printing || !selectedQuestions.some(item => item.printablePart === 'W1')} className="inline-flex items-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-violet-700 shadow-sm ring-1 ring-violet-200 hover:bg-violet-100 disabled:opacity-50">
                    <Printer className="mr-2 h-4 w-4" />打印学生练习版 PDF
                  </button>
                  <button onClick={() => void printSelected('teacher', true)} disabled={printing || !selectedQuestions.some(item => item.printablePart === 'W1')} className="inline-flex items-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-violet-700 shadow-sm ring-1 ring-violet-200 hover:bg-violet-100 disabled:opacity-50">
                    <Printer className="mr-2 h-4 w-4" />打印教师答案版 PDF
                  </button>
                  <button
                    onClick={() => void downloadGrammarWorkbook('student')}
                    disabled={generatingWorkbook !== null}
                    className="inline-flex items-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-violet-700 shadow-sm ring-1 ring-violet-200 hover:bg-violet-100 disabled:opacity-50"
                  >
                    <FileDown className="mr-2 h-4 w-4" />
                    {generatingWorkbook === 'student' ? 'Generating...' : '下载学生练习版 Word'}
                  </button>
                  <button
                    onClick={() => void downloadGrammarWorkbook('teacher')}
                    disabled={generatingWorkbook !== null}
                    className="inline-flex items-center rounded-lg bg-violet-700 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50"
                  >
                    <FileDown className="mr-2 h-4 w-4" />
                    {generatingWorkbook === 'teacher' ? 'Generating...' : '下载教师答案版 Word'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-5">
          {loading ? (
            <div className="py-16 text-center text-slate-500">Loading wrong questions...</div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          ) : visibleQuestions.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-6 py-10 text-center text-slate-500">
              No wrong questions found for this exam type and level.
            </div>
          ) : (
            <div className="space-y-4">
              {visibleQuestions.map((item, index) => {
                const isSelected = selectedQuestionIds.includes(item.id);
                const mediaUrl = resolveMediaUrl(item.media);

                return (
                  <div
                    key={item.id}
                    className={`rounded-2xl border p-5 shadow-sm transition ${
                      isSelected ? 'border-indigo-300 bg-indigo-50/40' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <button
                        type="button"
                        onClick={() => toggleQuestion(item.id)}
                        className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                          isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white text-transparent'
                        }`}
                        aria-label={isSelected ? 'Deselect question' : 'Select question'}
                      >
                        <CheckSquare className="h-4 w-4" />
                      </button>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                            Question {index + 1}
                          </span>
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                            {item.printablePart}
                          </span>
                          <span className="text-xs text-slate-400">{item.question_id}</span>
                        </div>

                        <div className="mt-2 text-xs text-slate-400">
                          Source: {new Date(item.source_test_date).toLocaleString()} · {item.source_test_label}
                        </div>

                        {bankVersion === 'CSCA' ? (
                          <MathText content={item.question_text} className="mt-3 block text-base font-medium leading-7 text-slate-900" />
                        ) : (
                          <div className="mt-3 whitespace-pre-line text-base font-medium leading-7 text-slate-900">
                            <AdminWrongText text={item.question_text} />
                          </div>
                        )}

                        {mediaUrl && (
                          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 break-all">
                            {resolveWrongImage(item.media) ? <AdminWrongText text={item.media || ''} /> : <>Media: {mediaUrl}</>}
                          </div>
                        )}

                        {item.printablePart === 'W2' && w2Guides.has(item.question_id) && (
                          <W2SentenceGuide guide={w2Guides.get(item.question_id)!} />
                        )}

                        {item.options.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {item.options.map((option, optionIndex) => (
                              <div key={`${item.id}-${optionIndex}`} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-500">
                                  {String.fromCharCode(65 + optionIndex)}
                                </span>
                                {bankVersion === 'CSCA' ? <MathText content={option} /> : <AdminWrongText text={option} />}
                              </div>
                            ))}
                          </div>
                        )}

                        {bankVersion === 'CSCA' && (
                          <div className="mt-4 text-sm text-slate-600">
                            Student answer: {item.chosen_option_index == null ? 'Unanswered' : String.fromCharCode(65 + item.chosen_option_index)} · Correct answer: {item.correct_option_index == null ? '-' : (item.correctAnswers || [item.correct_option_index]).map(answer => String.fromCharCode(65 + answer)).join(' / ')}
                          </div>
                        )}

                        {bankVersion === 'CSCA' && item.explanation && (
                          <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                            <span className="mb-1 block font-semibold">Explanation</span>
                            <MathText content={explanationLanguage === 'ID' && item.explanation_id ? item.explanation_id : item.explanation} />
                            {explanationLanguage === 'ID' && !item.explanation_id && <p className="mt-2 text-xs">Terjemahan ID belum tersedia; pembahasan CN ditampilkan.</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminStudentWrongHistory;
